import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { Inbox } from './Inbox'
import { TeamChatPanel } from '../components/chat/TeamChatPanel'
import { usePermissions } from '../hooks/usePermissions'
import { supabase } from '../lib/supabase'
import { Loader2Icon } from 'lucide-react'

type MessagesTab = 'external' | 'internal'

const TAB_STORAGE_KEY = 'rbcfs.messages.tab'

function readStoredTab(): MessagesTab | null {
  try {
    const v = localStorage.getItem(TAB_STORAGE_KEY)
    if (v === 'external' || v === 'internal') return v
  } catch {
    /* ignore */
  }
  return null
}

function storeTab(tab: MessagesTab) {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, tab)
  } catch {
    /* ignore */
  }
}

export function Messages() {
  const { id: routeId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { can, loading: permLoading } = usePermissions()
  const canInbox = can('communication.inbox')
  const canChat = can('communication.chat')

  const tabParam = searchParams.get('tab') as MessagesTab | null
  const channelParam = searchParams.get('channel')
  const idParam = searchParams.get('id') || routeId || null

  const availableTabs = useMemo(() => {
    const tabs: MessagesTab[] = []
    if (canInbox) tabs.push('external')
    if (canChat || canInbox) tabs.push('internal')
    return tabs
  }, [canInbox, canChat])

  const defaultTab: MessagesTab = useMemo(() => {
    if (tabParam && availableTabs.includes(tabParam)) return tabParam
    const stored = readStoredTab()
    if (stored && availableTabs.includes(stored)) return stored
    if (channelParam && availableTabs.includes('internal')) return 'internal'
    return availableTabs[0] || 'external'
  }, [tabParam, availableTabs, channelParam])

  const [activeTab, setActiveTab] = useState<MessagesTab>(defaultTab)
  const [externalUnread, setExternalUnread] = useState(0)
  const [internalUnread, setInternalUnread] = useState(0)

  useEffect(() => {
    setActiveTab(defaultTab)
  }, [defaultTab])

  const selectTab = useCallback(
    (tab: MessagesTab) => {
      setActiveTab(tab)
      storeTab(tab)
      const next = new URLSearchParams(searchParams)
      next.set('tab', tab)
      if (tab === 'external') next.delete('channel')
      else next.delete('id')
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const onInternalChannelChange = useCallback(
    (channelId: string | null) => {
      const next = new URLSearchParams(searchParams)
      next.set('tab', 'internal')
      if (channelId) next.set('channel', channelId)
      else next.delete('channel')
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const refreshUnread = useCallback(async () => {
    if (canInbox) {
      try {
        const { data } = await supabase
          .from('conversations')
          .select('unread_count')
          .in('state', ['active'])
        const sum = (data || []).reduce((acc, r) => acc + (r.unread_count || 0), 0)
        setExternalUnread(sum)
      } catch {
        setExternalUnread(0)
      }
    }
    if (canChat || canInbox) {
      try {
        const { data: session } = await supabase.auth.getUser()
        const uid = session.user?.id
        if (!uid) {
          setInternalUnread(0)
          return
        }
        const { data: reads } = await supabase
          .from('chat_channel_reads')
          .select('channel_id, last_read_at')
          .eq('user_id', uid)
        const readMap = new Map((reads || []).map((r) => [r.channel_id, r.last_read_at]))
        const { data: channels } = await supabase.from('chat_channels').select('id')
        let unread = 0
        for (const ch of channels || []) {
          const since = readMap.get(ch.id)
          let q = supabase
            .from('chat_messages')
            .select('id', { count: 'exact', head: true })
            .eq('channel_id', ch.id)
            .neq('sender_id', uid)
          if (since) q = q.gt('created_at', since)
          const { count } = await q
          if (count && count > 0) unread += count
        }
        setInternalUnread(unread)
      } catch {
        setInternalUnread(0)
      }
    }
  }, [canInbox, canChat])

  useEffect(() => {
    if (permLoading) return
    refreshUnread()
    const t = window.setInterval(refreshUnread, 60000)
    return () => window.clearInterval(t)
  }, [permLoading, refreshUnread])

  if (permLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!canInbox && !canChat) {
    return <Navigate to="/access-denied" replace />
  }

  const tabChrome = (
    <>
      <h1 className="text-xs font-semibold text-gray-900 shrink-0">Messages</h1>
      <div className="flex items-center gap-0 min-w-0">
        {availableTabs.includes('external') && (
          <button
            type="button"
            onClick={() => selectTab('external')}
            className={`px-2 py-1 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'external'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            External
            {externalUnread > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold">
                {externalUnread > 99 ? '99+' : externalUnread}
              </span>
            )}
          </button>
        )}
        {availableTabs.includes('internal') && (
          <button
            type="button"
            onClick={() => selectTab('internal')}
            className={`px-2 py-1 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'internal'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Internal
            {internalUnread > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold">
                {internalUnread > 99 ? '99+' : internalUnread}
              </span>
            )}
          </button>
        )}
      </div>
    </>
  )

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col bg-white min-h-0">
      {activeTab === 'external' && canInbox ? (
        <div className="flex-1 min-h-0 overflow-hidden">
          <Inbox embedded hubConversationId={idParam} embeddedLeading={tabChrome} />
        </div>
      ) : activeTab === 'internal' ? (
        <>
          <div className="flex-shrink-0 border-b border-gray-200 px-3 sm:px-4 flex items-center gap-2 py-1 min-h-0">
            {tabChrome}
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <TeamChatPanel
              embedded
              initialChannelId={channelParam}
              onActiveChannelChange={onInternalChannelChange}
            />
          </div>
        </>
      ) : (
        <div className="p-6 text-sm text-gray-500">No messages access for this tab.</div>
      )}
    </div>
  )
}
